import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import useCanvasStore from '../stores/canvasStore';

// Double-space detection threshold
const DOUBLE_TAP_THRESHOLD = 400; // ms

// Elements where the browser's own Space action is not a scroll (click, open, play),
// so it must not be blocked. Links and ARIA roles are left out on purpose: Space
// scrolls on them natively, and role-based components handle Space in their own code.
const SPACE_ACTION_CONTROLS = 'button, select, summary, video, audio';

// Button indices (negative = button, positive = item)
const BUTTON = { folder: -3, sheet: -2, doc: -1 };

/**
 * Parse focus index string "depth_index" -> { depth, index }
 */
const parseFocus = (focusedIndex) => {
    if (!focusedIndex) return null;
    const [depth, index] = focusedIndex.split('_').map(Number);
    return { depth, index };
};

/**
 * Create focus index string from depth and index
 */
const createFocus = (depth, index) => `${depth}_${index}`;

/**
 * Keyboard navigation hook for Zustand canvas store
 * Now uses Zustand for clipboard operations instead of Redux
 */
export const useCanvasNavigation = ({
    scrollContainerRef,
    folderId,
    onBulkAssign,
}) => {
    const navigate = useNavigate();
    
    // Zustand state
    const items = useCanvasStore(s => s.items);
    const selectedPath = useCanvasStore(s => s.selectedPath);
    const focusedIndex = useCanvasStore(s => s.focusedIndex);
    const currentDepth = useCanvasStore(s => s.currentDepth);
    const selectedItems = useCanvasStore(s => s.selectedItems);
    const focusMode = useCanvasStore(s => s.focusMode);
    const bookmarkViewActive = useCanvasStore(s => s.bookmarkViewActive);
    const currentFolder = useCanvasStore(s => s.currentFolder);
    const buttonHandlers = useCanvasStore(s => s.buttonHandlers);  // { [depth]: { folder: fn, sheet: fn, doc: fn } }
    
    // Zustand actions
    const setFocusedIndex = useCanvasStore(s => s.setFocusedIndex);
    const setCurrentDepth = useCanvasStore(s => s.setCurrentDepth);
    const toggleStarLevel = useCanvasStore(s => s.toggleStarLevel);
    const toggleArchiveStatus = useCanvasStore(s => s.toggleArchiveStatus);
    const clearSelection = useCanvasStore(s => s.clearSelection);
    const clearClipboard = useCanvasStore(s => s.clearClipboard);
    const copyItems = useCanvasStore(s => s.copyItems);
    const cutItems = useCanvasStore(s => s.cutItems);
    const pasteItems = useCanvasStore(s => s.pasteItems);
    const toggleFocusMode = useCanvasStore(s => s.toggleFocusMode);
    const setOpenDetailPaneId = useCanvasStore(s => s.setOpenDetailPaneId);
    
    // Ref for double-space detection
    const lastSpaceTimeRef = useRef(0);

    /**
     * Get item count at a depth
     */
    const getItemCount = useCallback((depth) => {
        return (items[depth] || []).length;
    }, [items]);

    /**
     * Get item at depth and index
     */
    const getItem = useCallback((depth, index) => {
        const depthItems = items[depth] || [];
        return depthItems[index] || null;
    }, [items]);

    /**
     * Move focus up/down within current column (with circular navigation)
     */
    const moveFocusVertical = useCallback((direction) => {
        const parsed = parseFocus(focusedIndex);
        const depth = parsed?.depth ?? currentDepth;
        const index = parsed?.index ?? BUTTON.folder;
        
        const itemCount = getItemCount(depth);
        let newIndex;
        
        if (direction < 0) {
            // Moving up
            if (index === 0) {
                newIndex = BUTTON.doc; // First item -> last button
            } else if (index === BUTTON.folder) {
                // At top button, wrap to last item (circular navigation)
                newIndex = itemCount > 0 ? itemCount - 1 : BUTTON.doc;
            } else {
                newIndex = index - 1;
            }
        } else {
            // Moving down
            if (index === BUTTON.doc) {
                newIndex = itemCount > 0 ? 0 : BUTTON.folder; // Last button -> first item (or wrap to top if no items)
            } else if (index >= 0 && index >= itemCount - 1) {
                // At last item, wrap to first button (circular navigation)
                newIndex = BUTTON.folder;
            } else {
                newIndex = index + 1;
            }
        }
        
        setFocusedIndex(createFocus(depth, newIndex));
    }, [focusedIndex, currentDepth, getItemCount, setFocusedIndex]);

    /**
     * Move focus left (to parent column) - goes directly to items, not buttons
     */
    const moveFocusLeft = useCallback(() => {
        const parsed = parseFocus(focusedIndex);
        const depth = parsed?.depth ?? currentDepth;
        
        // Go to parent column
        if (depth <= 1) return; // Already at root
        
        const newDepth = depth - 1;
        const parentItems = items[newDepth] || [];
        
        // Find which item in parent column leads to current column
        let parentIndex = 0;
        if (parentItems.length > 0 && selectedPath.length >= newDepth) {
            const parentId = selectedPath[newDepth - 1];
            const foundIndex = parentItems.findIndex(item => item.id === parentId);
            if (foundIndex >= 0) parentIndex = foundIndex;
        }
        
        setFocusedIndex(createFocus(newDepth, parentIndex));
        setCurrentDepth(newDepth);
        
        // Navigate URL to parent
        if (newDepth === 1) {
            navigate('/canvas');
        } else {
            const parentFolder = parentItems[parentIndex];
            if (parentFolder) {
                navigate(`/folder/${parentFolder.id}`);
            }
        }
    }, [focusedIndex, currentDepth, items, selectedPath, setFocusedIndex, setCurrentDepth, navigate]);

    /**
     * Move focus right (to child column) - goes directly to items, not buttons
     */
    const moveFocusRight = useCallback(() => {
        const parsed = parseFocus(focusedIndex);
        const depth = parsed?.depth ?? currentDepth;
        const index = parsed?.index ?? 0;
        
        // On button: do nothing (use up/down to navigate buttons)
        if (index < 0) return;
        
        // On item: try to move to child column
        const item = getItem(depth, index);
        if (!item || item.item_type !== 'folder') return;
        
        const newDepth = depth + 1;
        
        // Always navigate to the folder - this ensures we load the correct children
        navigate(`/folder/${item.id}`);
        // Focus first item (0) in the new column, not buttons
        setFocusedIndex(createFocus(newDepth, 0));
        setCurrentDepth(newDepth);
    }, [focusedIndex, currentDepth, getItem, setFocusedIndex, setCurrentDepth, navigate]);

    /**
     * Handle Enter key - open detail pane or activate button
     */
    const handleEnter = useCallback(() => {
        const parsed = parseFocus(focusedIndex);
        
        if (!parsed) {
            // No focus - open current folder detail if we have one
            if (folderId) {
                setOpenDetailPaneId(parseInt(folderId, 10));
            }
            return;
        }
        
        const { depth, index } = parsed;
        
        if (index < 0) {
            // Activate button handler
            const depthHandlers = buttonHandlers[depth];
            if (depthHandlers) {
                const buttonType = index === BUTTON.folder ? 'folder' : 
                                   index === BUTTON.sheet ? 'sheet' : 'doc';
                depthHandlers[buttonType]?.();
            }
        } else {
            // Open item detail pane - set focus and open details
            const item = getItem(depth, index);
            if (item) {
                // Ensure focus is set to this item
                setFocusedIndex(createFocus(depth, index));
                setCurrentDepth(depth);
                
                // For shortcuts, navigate to the original item instead of opening details
                if (item.item_type === 'shortcut' && item.original_item) {
                    navigate(`/folder/${item.original_item}`);
                } else if (item.item_type === 'folder') {
                    // For folders: check if children are already opened
                    const isFolderOpen = selectedPath && selectedPath.includes(item.id);
                    
                    if (!isFolderOpen) {
                        // Children not opened - navigate into folder to open them
                        const newDepth = depth + 1;
                        navigate(`/folder/${item.id}`);
                        // Focus first item (0) in the new column
                        setFocusedIndex(createFocus(newDepth, 0));
                        setCurrentDepth(newDepth);
                    } else {
                        // Children already opened - open detail pane
                        setOpenDetailPaneId(item.id);
                    }
                } else {
                    // For links, open detail pane using Zustand
                    setOpenDetailPaneId(item.id);
                }
            }
        }
    }, [focusedIndex, folderId, buttonHandlers, getItem, selectedPath, setOpenDetailPaneId, setFocusedIndex, setCurrentDepth, navigate]);

    /**
     * Toggle star level on focused item
     */
    const handleToggleStar = useCallback(() => {
        const parsed = parseFocus(focusedIndex);
        if (!parsed || parsed.index < 0) return;
        
        const item = getItem(parsed.depth, parsed.index);
        if (item) {
            toggleStarLevel(item, parsed.depth);
        }
    }, [focusedIndex, getItem, toggleStarLevel]);

    /**
     * Toggle archive on focused item
     */
    const handleToggleArchive = useCallback(() => {
        const parsed = parseFocus(focusedIndex);
        if (!parsed || parsed.index < 0) return;
        
        const item = getItem(parsed.depth, parsed.index);
        if (item) {
            toggleArchiveStatus(item, parsed.depth);
        }
    }, [focusedIndex, getItem, toggleArchiveStatus]);

    /**
     * Main keyboard handler
     */
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Skip if typing in input
            const tag = e.target.tagName.toLowerCase();
            if (tag === 'input' || tag === 'textarea' || e.target.isContentEditable) {
                return;
            }
            const selectionNode = window.getSelection()?.anchorNode;
            const selectionHost = selectionNode?.parentElement || selectionNode;
            if (e.target.closest?.('[data-agent-chat]') || selectionHost?.closest?.('[data-agent-chat]')) {
                return;
            }

            // Block only Space's page-scroll on the canvas (including held-down repeats that
            // return early below); controls keep their own Space action (click, toggle, play)
            if (e.key === ' ' && !e.target.closest(SPACE_ACTION_CONTROLS)) {
                e.preventDefault();
            }

            // Allow repeats only for arrow up/down (continuous navigation when held)
            const allowRepeat = e.key === 'ArrowUp' || e.key === 'ArrowDown';
            if (e.repeat && !allowRepeat) return;
            
            switch (e.key) {
                case 'ArrowUp':
                    e.preventDefault();
                    moveFocusVertical(-1);
                    break;
                    
                case 'ArrowDown':
                    e.preventDefault();
                    moveFocusVertical(1);
                    break;
                    
                case 'ArrowLeft':
                    e.preventDefault();
                    moveFocusLeft();
                    break;
                    
                case 'ArrowRight':
                    e.preventDefault();
                    moveFocusRight();
                    break;
                    
                case 'Enter':
                    e.preventDefault();
                    handleEnter();
                    break;
                    
                case 'q':
                case 'Q':
                    if (e.ctrlKey || e.metaKey) {
                        e.preventDefault();
                        handleToggleStar();
                    }
                    break;
                    
                case 'a':
                case 'A':
                    if (e.ctrlKey || e.metaKey) {
                        e.preventDefault();
                        if (selectedItems.length >= 1 && onBulkAssign) {
                            onBulkAssign();
                        }
                    } else if (e.altKey) {
                        e.preventDefault();
                        handleToggleArchive();
                    }
                    break;
                    
                case 'c':
                case 'C':
                    if (e.ctrlKey || e.metaKey) {
                        e.preventDefault();
                        // Use Zustand clipboard
                        copyItems(folderId);
                    }
                    break;
                    
                case 'x':
                case 'X':
                    if (e.ctrlKey || e.metaKey) {
                        e.preventDefault();
                        // Use Zustand clipboard
                        cutItems(folderId);
                    }
                    break;
                    
                case 'v':
                case 'V':
                    if (e.ctrlKey || e.metaKey) {
                        e.preventDefault();
                        // Use Zustand paste
                        pasteItems(folderId);
                    }
                    break;
                    
                case 'Escape':
                    // If in focus mode, exit it first
                    if (focusMode) {
                        e.preventDefault();
                        toggleFocusMode();
                        return;
                    }
                    clearSelection();
                    clearClipboard();
                    break;
                
                case ' ':
                    // Double-space detection for focus mode toggle (canvas only, not the bookmark view)
                    if (!bookmarkViewActive) {
                        const now = Date.now();
                        if (now - lastSpaceTimeRef.current < DOUBLE_TAP_THRESHOLD) {
                            toggleFocusMode();
                            
                            // When entering focus mode, URL will be the source of truth
                            // FocusModeView will handle displaying the correct item based on URL params
                            lastSpaceTimeRef.current = 0; // Reset to prevent triple-tap issues
                        } else {
                            lastSpaceTimeRef.current = now;
                        }
                    }
                    break;
                    
                default:
                    break;
            }
        };
        
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [
        moveFocusVertical,
        moveFocusLeft,
        moveFocusRight,
        handleEnter,
        handleToggleStar,
        handleToggleArchive,
        selectedItems,
        folderId,
        onBulkAssign,
        clearSelection,
        clearClipboard,
        copyItems,
        cutItems,
        pasteItems,
        focusMode,
        bookmarkViewActive,
        focusedIndex,
        items,
        currentFolder,
        selectedPath,
        currentDepth,
        toggleFocusMode,
        setFocusedIndex
    ]);
    
    // Focus mode navigation is now handled by FocusModeView component
    // It uses URL params as the single source of truth

    /**
     * Auto-scroll to keep current depth visible.
     * The first run is skipped so the board can settle on a whole column.
     */
    const skipInitialColumnScroll = useRef(true);
    useEffect(() => {
        if (skipInitialColumnScroll.current) {
            skipInitialColumnScroll.current = false;
            return;
        }
        if (!scrollContainerRef?.current) return;
        
        const container = scrollContainerRef.current;
        const columnEl = container.querySelector(`[data-depth="${currentDepth}"]`);
        if (!columnEl) return;
        
        const containerRect = container.getBoundingClientRect();
        const columnRect = columnEl.getBoundingClientRect();
        const fullyVisible = columnRect.left >= containerRect.left - 1
            && columnRect.right <= containerRect.right + 1;
        if (fullyVisible) return;
        container.scrollLeft += columnRect.left - containerRect.left;
    }, [currentDepth, scrollContainerRef]);

    return {
        focusedIndex,
        currentDepth,
        focusMode,
        parseFocus,
        createFocus,
    };
};

export default useCanvasNavigation;
